'use client';

/**
 * InputGroup — Shadcn-style input group (no Base UI root).
 * Composites: InputGroup, Addon, Button, Input, InputGroupText, TextArea.
 * Place addons after the input/textarea in DOM; use align to position. Use data-slot="input-group-control" for focus handling.
 */
import { Input as BaseInput } from '@base-ui/react/input';
import * as stylex from '@stylexjs/stylex';
import * as React from 'react';
import { Button as ButtonComponent } from '../../../Buttons';
import { Loader as LoaderComponent } from '../../../Loader/Loader';
import '../../form.tokens.stylex';
import { formTokens } from '../../form.tokens.stylex';
import '../../../../styles/tokens.stylex';
import { spacing, VP } from '../../../../styles/tokens.stylex';
import { formStyles } from '../../form.styles';
import { inputGroupMarker } from '../../markers.stylex';
import { FieldControlContext } from './Field';
import { use } from 'react';
import { RawInput as RawInputPrimitive } from './Input';
const INPUT_GROUP_CONTROL_SLOT = 'input-group-control';
export type InputGroupAlign = 'inline-start' | 'inline-end' | 'block-start' | 'block-end';
export type InputGroupRootProps = React.ComponentProps<'div'> & {
  styles?: stylex.StyleXStyles;
};
export type InputGroupInputProps = BaseInput.Props & {
  styles?: stylex.StyleXStyles;
};
export type AddonProps = React.ComponentProps<'div'> & {
  /** Visual position. Addon must stay after input in DOM for focus order. */
  align?: InputGroupAlign;
  styles?: stylex.StyleXStyles;
};
export type ButtonProps = React.ComponentProps<typeof ButtonComponent> & {
  styles?: stylex.StyleXStyles;
};
export type InputProps = BaseInput.Props & {
  styles?: stylex.StyleXStyles;
};
export type InputGroupTextProps = React.ComponentProps<'span'> & {
  styles?: stylex.StyleXStyles;
};
export type TextAreaProps = Omit<React.ComponentProps<typeof BaseInput>, 'render'> & {
  /** Rendered as textarea. */
  render?: never;
  styles?: stylex.StyleXStyles;
};
function Root({
  children,
  styles,
  ...props
}: InputGroupRootProps) {
  return <div {...stylex.props(inputGroupMarker, formStyles.input, sx.root, styles)} {...props}>
      {children}
    </div>;
}
function Addon({
  children,
  align = 'inline-start',
  styles,
  ...props
}: AddonProps) {
  const alignStyle = align === 'inline-start' ? sx.addonInlineStart : align === 'inline-end' ? sx.addonInlineEnd : align === 'block-start' ? sx.addonBlockStart : sx.addonBlockEnd;
  return <div data-align={align} {...stylex.props(sx.addon, alignStyle, styles)} {...props}>
      {children}
    </div>;
}
function Button({
  children,
  styles,
  variant = 'ghost',
  size = 'extra-compact',
  ...props
}: ButtonProps) {
  return <ButtonComponent variant={variant} size={size} {...stylex.props(sx.button, styles)} {...props}>
      {children}
    </ButtonComponent>;
}
function RawInput({
  styles,
  ...props
}: InputProps) {
  return <RawInputPrimitive {...props} styles={[sx.inputControl, styles]} />;
}
function Input(props: InputProps) {
  const fieldControl = use(FieldControlContext);
  return <RawInput {...fieldControl} {...props} />;
}
function InputGroupText({
  children,
  styles,
  ...props
}: InputGroupTextProps) {
  return <span {...stylex.props(sx.text, styles)} {...props}>
      {children}
    </span>;
}
function TextArea({
  styles,
  ...props
}: TextAreaProps) {
  return <BaseInput data-slot={INPUT_GROUP_CONTROL_SLOT} render={<textarea />} {...stylex.props(formStyles.input, sx.textareaControl, styles)} {...props} />;
}
function Loader() {
  return <LoaderComponent size={'1.25em'} />;
}
export const InputGroup = {
  Root,
  Addon: Addon,
  Button: Button,
  RawInput: RawInput,
  Input: Input,
  Text: InputGroupText,
  Textarea: TextArea,
  Loader: Loader
};
const sx = {
  root: {
    $$css: true
  },
  addon: {
    kOIVth: "x3ybb33",
    k8WAf4: "xksb554",
    kGNEyG: "x6s0dn4",
    kWkggS: "x1o47r4",
    kMwMTN: "x143ll9a",
    k1xSpc: "x1s85apg xl8lx7d",
    kmuXW: "x2lah0s",
    $$css: true
  },
  addonInlineStart: {
    kayTVb: "x1clqncf",
    kwRFfy: "x41045o",
    kZCmMZ: "x1qudamw",
    $$css: true
  },
  addonInlineEnd: {
    kayTVb: "xo1ph6p",
    kwRFfy: "x41045o",
    kZCmMZ: "x1qudamw",
    $$css: true
  },
  addonBlockStart: {
    kg3NbH: "x1riykw2",
    kCS8Yb: "x1l7klhg",
    kayTVb: "x1g77sc7",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  addonBlockEnd: {
    kg3NbH: "x1riykw2",
    kCS8Yb: "x1l7klhg",
    kayTVb: "x182iqb8",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  button: {
    kmuXW: "x2lah0s",
    $$css: true
  },
  inputControl: {
    ksu8eU: "xng3xce",
    kMzoRj: "xc342km",
    kCS8Yb: "x1r8uery",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kayTVb: "x14yy4lh",
    $$css: true
  },
  textareaControl: {
    kCS8Yb: "x1r8uery",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kayTVb: "x14yy4lh",
    kHenm0: "x288g5",
    kAzted: "xyfbndg",
    $$css: true
  },
  text: {
    kGuDYH: "x1qlqyl8",
    kLWn49: "x15bjb6t",
    $$css: true
  }
};